import { useNavigate } from 'react-router-dom';
import { date, humanise, mobile as fmtMobile, num } from '../lib/format';
import { DataTable, useListParams } from '../components/DataTable';
import type { Column } from '../components/DataTable';
import { Avatar, Badge, SearchInput, Select, StatusBadge } from '../components/ui';

interface UserRow {
  id: string;
  mobile: string;
  displayName: string | null;
  email: string | null;
  profileImage: string | null;
  roles: string[];
  kycStatus: string;
  isBlocked: boolean;
  createdAt: string;
  lastLoginAt: string | null;
  /** Where they are, and where that was learned: the app, or a registration. */
  location: { label: string; source: 'app' | 'partner' | 'member' } | null;
  _count: { listings: number; payments: number };
}

const LOCATION_SOURCE = {
  app: 'Chosen in the app',
  partner: 'From partner registration',
  member: 'From community membership',
} as const;

export function UsersPage() {
  const { get, set } = useListParams();
  const navigate = useNavigate();
  const search = get('search');
  const role = get('role');
  const kycStatus = get('kycStatus');
  const blocked = get('blocked');
  const location = get('location');

  const columns: Array<Column<UserRow>> = [
    {
      key: 'user',
      header: 'User',
      render: (r) => (
        <div className="row">
          <Avatar name={r.displayName} src={r.profileImage} />
          <div>
            <div className="cell-strong">
              {r.displayName ?? <span className="cell-muted">No name set</span>}
              {r.isBlocked && (
                <span style={{ marginLeft: 7 }}>
                  <Badge tone="red">Blocked</Badge>
                </span>
              )}
            </div>
            <div className="cell-muted">{fmtMobile(r.mobile)}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'roles',
      header: 'Roles',
      render: (r) => (
        <div className="row" style={{ gap: 4, flexWrap: 'wrap' }}>
          {r.roles.map((role) => (
            <StatusBadge key={role} value={role} />
          ))}
        </div>
      ),
    },
    {
      key: 'location',
      header: 'Location',
      render: (r) =>
        r.location ? (
          <div>
            <div>{r.location.label}</div>
            <div className="cell-muted">{LOCATION_SOURCE[r.location.source]}</div>
          </div>
        ) : (
          <span className="cell-muted">Not set</span>
        ),
    },
    { key: 'kyc', header: 'KYC', render: (r) => <StatusBadge value={r.kycStatus} /> },
    { key: 'listings', header: 'Listings', numeric: true, render: (r) => num(r._count.listings) },
    { key: 'payments', header: 'Payments', numeric: true, render: (r) => num(r._count.payments) },
    { key: 'joined', header: 'Joined', render: (r) => <span className="cell-muted">{date(r.createdAt)}</span> },
    {
      key: 'seen',
      header: 'Last seen',
      render: (r) => <span className="cell-muted">{r.lastLoginAt ? date(r.lastLoginAt) : 'never'}</span>,
    },
  ];

  return (
    <DataTable<UserRow>
      queryKey={['users', search, role, kycStatus, blocked, location]}
      path="/admin/users"
      params={{ search, role, kycStatus, blocked, location }}
      columns={columns}
      onRowClick={(r) => navigate(`/users/${r.id}`)}
      emptyIcon="☖"
      emptyTitle="No users match"
      emptyBody="Try clearing the filters."
      toolbar={
        <>
          <SearchInput value={search} onChange={(v) => set('search', v)} placeholder="Name, mobile or email…" />
          <SearchInput value={location} onChange={(v) => set('location', v)} placeholder="Area, city or state…" />
          <Select
            value={role}
            placeholder="All roles"
            onChange={(e) => set('role', e.target.value)}
            options={['CUSTOMER', 'SELLER', 'SERVICE_PROVIDER', 'AGENT', 'ADMIN'].map((v) => ({
              value: v,
              label: humanise(v),
            }))}
          />
          <Select
            value={kycStatus}
            placeholder="Any KYC status"
            onChange={(e) => set('kycStatus', e.target.value)}
            options={['NOT_STARTED', 'PENDING', 'VERIFIED', 'REJECTED'].map((v) => ({ value: v, label: humanise(v) }))}
          />
          <Select
            value={blocked}
            placeholder="Active and blocked"
            onChange={(e) => set('blocked', e.target.value)}
            options={[
              { value: 'false', label: 'Active only' },
              { value: 'true', label: 'Blocked only' },
            ]}
          />
        </>
      }
    />
  );
}
